import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Copy } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const WORKFLOW = ["Explore Course", "Apply", "Application ID", "Counselling", "Document Verification", "Admission Confirmation", "Fee Payment", "Student ID", "Learning Portal"];

export default function Apply() {
  const [searchParams] = useSearchParams();
  const [courses, setCourses] = useState([]);
  const [form, setForm] = useState({
    full_name: "", dob: "", gender: "", phone: "", email: "", address: "",
    district: "", state: "Assam", qualification: "", course_slug: searchParams.get("course") || "",
    batch_preference: "", mode: "Offline", referral_source: "Website",
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/courses", { params: { limit: 50 } }).then((r) => setCourses(r.data.items)).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/applications", form);
      setResult(data);
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <div className="bg-navy-base min-h-screen" data-testid="apply-success-page">
        <div className="max-w-2xl mx-auto px-4 py-20">
          <div className="bg-card border border-emerald-500/40 rounded-3xl p-10 text-center glow-card">
            <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto mb-5" />
            <h1 className="font-display text-3xl font-extrabold text-white mb-2">Application Submitted</h1>
            <p className="text-sm text-slate-400 mb-6">{result.message}</p>
            <div className="bg-navy-deep border border-orange-500/40 rounded-2xl py-5 px-6 mb-6 inline-flex items-center gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 text-left">Your Application ID</div>
                <div className="font-mono text-2xl font-bold text-orange-400" data-testid="application-id">{result.application_id}</div>
              </div>
              <button onClick={() => { navigator.clipboard.writeText(result.application_id); toast.success("Copied!"); }}
                className="text-slate-400 hover:text-orange-400 transition-colors" data-testid="copy-application-id" aria-label="Copy Application ID">
                <Copy className="w-5 h-5" />
              </button>
            </div>
            <p className="text-xs text-slate-500 mb-8">Next steps: our counsellor will contact you on your phone/WhatsApp for counselling and document verification.</p>
            <div className="flex flex-wrap justify-center gap-3">
              <Button asChild className="bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-full px-7 transition-colors" data-testid="track-application-btn">
                <Link to="/portal">Student Portal</Link>
              </Button>
              <Button asChild variant="outline" className="border-white/20 text-white hover:bg-white/5 rounded-full px-7 transition-colors" data-testid="back-courses-btn">
                <Link to="/courses">Browse More Courses</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-navy-base min-h-screen" data-testid="apply-page">
      <SEO title="Admissions — Apply Online | XTM TECHNOLOGIES Jorhat"
        description="Apply online for courses at XTM FutureSkills Academy Jorhat. Get your Application ID instantly and track counselling, verification and enrollment." />
      <section className="relative hero-glow">
        <div className="absolute inset-0 cyber-grid opacity-40" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-orange-400 mb-2">Admissions Portal</div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white mb-6" data-testid="apply-heading">Apply for Admission</h1>
          <div className="flex flex-wrap items-center gap-1.5 max-w-4xl" data-testid="admission-workflow">
            {WORKFLOW.map((s, i) => (
              <span key={s} className="flex items-center gap-1.5">
                <span className={`text-[11px] font-medium rounded-full px-2.5 py-1 border ${i === 1 ? "bg-orange-600 border-orange-600 text-white" : "border-white/15 text-slate-400"}`}>{s}</span>
                {i < WORKFLOW.length - 1 && <span className="text-orange-500 text-xs">→</span>}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <form onSubmit={submit} className="bg-card border border-white/10 rounded-3xl p-7 sm:p-10 space-y-5" data-testid="application-form">
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Full Name *" id="ap-name"><Input id="ap-name" required value={form.full_name} onChange={(e) => set("full_name", e.target.value)} className="bg-navy-base border-white/15 text-white mt-1" data-testid="apply-name-input" /></Field>
            <Field label="Date of Birth *" id="ap-dob"><Input id="ap-dob" type="date" required value={form.dob} onChange={(e) => set("dob", e.target.value)} className="bg-navy-base border-white/15 text-white mt-1" data-testid="apply-dob-input" /></Field>
            <Field label="Gender *" id="ap-gender">
              <Select required value={form.gender} onValueChange={(v) => set("gender", v)}>
                <SelectTrigger className="bg-navy-base border-white/15 text-white mt-1" data-testid="apply-gender-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="bg-navy-surface border-white/10 text-slate-200">
                  {["Male", "Female", "Other", "Prefer not to say"].map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Phone (WhatsApp) *" id="ap-phone"><Input id="ap-phone" required value={form.phone} onChange={(e) => set("phone", e.target.value)} className="bg-navy-base border-white/15 text-white mt-1" data-testid="apply-phone-input" /></Field>
            <Field label="Email *" id="ap-email"><Input id="ap-email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} className="bg-navy-base border-white/15 text-white mt-1" data-testid="apply-email-input" /></Field>
            <Field label="Educational Qualification *" id="ap-qual">
              <Select required value={form.qualification} onValueChange={(v) => set("qualification", v)}>
                <SelectTrigger className="bg-navy-base border-white/15 text-white mt-1" data-testid="apply-qualification-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="bg-navy-surface border-white/10 text-slate-200">
                  {["Class 10", "Class 12 (appearing)", "Class 12 (passed)", "Diploma", "Graduate", "Postgraduate", "Other"].map((q) => <SelectItem key={q} value={q}>{q}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Address *" id="ap-address"><Input id="ap-address" required value={form.address} onChange={(e) => set("address", e.target.value)} className="bg-navy-base border-white/15 text-white mt-1" data-testid="apply-address-input" /></Field>
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="District *" id="ap-district"><Input id="ap-district" required value={form.district} onChange={(e) => set("district", e.target.value)} className="bg-navy-base border-white/15 text-white mt-1" data-testid="apply-district-input" /></Field>
            <Field label="State *" id="ap-state"><Input id="ap-state" required value={form.state} onChange={(e) => set("state", e.target.value)} className="bg-navy-base border-white/15 text-white mt-1" data-testid="apply-state-input" /></Field>
            <Field label="Course Preference *" id="ap-course">
              <Select required value={form.course_slug} onValueChange={(v) => set("course_slug", v)}>
                <SelectTrigger className="bg-navy-base border-white/15 text-white mt-1" data-testid="apply-course-select"><SelectValue placeholder="Select course" /></SelectTrigger>
                <SelectContent className="bg-navy-surface border-white/10 text-slate-200 max-h-72">
                  {courses.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Preferred Learning Mode *" id="ap-mode">
              <Select required value={form.mode} onValueChange={(v) => set("mode", v)}>
                <SelectTrigger className="bg-navy-base border-white/15 text-white mt-1" data-testid="apply-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-navy-surface border-white/10 text-slate-200">
                  {["Offline", "Online", "Hybrid", "Work-Integrated"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Batch Preference" id="ap-batch"><Input id="ap-batch" placeholder="e.g. Morning batch, July" value={form.batch_preference} onChange={(e) => set("batch_preference", e.target.value)} className="bg-navy-base border-white/15 text-white mt-1" data-testid="apply-batch-input" /></Field>
            <Field label="How did you hear about us?" id="ap-ref">
              <Select value={form.referral_source} onValueChange={(v) => set("referral_source", v)}>
                <SelectTrigger className="bg-navy-base border-white/15 text-white mt-1" data-testid="apply-referral-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-navy-surface border-white/10 text-slate-200">
                  {["Website", "WhatsApp", "Google", "Social Media", "Workshop", "Referral", "Walk-in", "Other"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>
          <p className="text-xs text-slate-500">Document upload (photograph, ID proof, certificates) happens after counselling through the Student Portal. By applying you agree to be contacted about your application.</p>
          <Button type="submit" disabled={submitting} className="w-full bg-orange-600 hover:bg-orange-700 text-white font-bold py-6 rounded-xl transition-colors" data-testid="apply-submit-btn">
            {submitting ? "Submitting…" : "Submit Application"}
          </Button>
        </form>
      </section>
    </div>
  );
}

function Field({ label, id, children }) {
  return (
    <div>
      <Label htmlFor={id} className="text-slate-300">{label}</Label>
      {children}
    </div>
  );
}
